if (!customElements.get('lp-pdp-personalize-drawer')) {
  customElements.define(
    'lp-pdp-personalize-drawer',
    class LpPdpPersonalizeDrawer extends HTMLElement {
      constructor() {
        super();
        this._onOpenClick = this._onOpenClick.bind(this);
        this._onOverlayClick = this._onOverlayClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onDrawerAtc = this._onDrawerAtc.bind(this);
        this._onFieldChange = this._onFieldChange.bind(this);
        this._onVariantSync = this._onVariantSync.bind(this);
      }

      connectedCallback() {
        const wrapperId = this.dataset.formWrapperId;
        const wrapper = wrapperId ? document.getElementById(wrapperId) : null;
        this.form = wrapper?.querySelector('#lp-product-form');
        this.openBtn = wrapper?.querySelector('[data-js-open-personalize]');
        this.variantInput = this.form?.querySelector('[name="id"]');
        this.variantDataEl = this.form?.querySelector('#lp-variant-data');

        if (!this._portaledToBody) {
          this._portaledToBody = true;
          document.body.appendChild(this);
        }

        this.overlay = this.querySelector('[data-js-personalize-overlay]');
        this.panel = this.querySelector('[data-js-personalize-panel]');
        this.closeBtn = this.querySelector('[data-js-personalize-close]');
        this.drawerAtcBtn = this.querySelector('[data-js-drawer-atc]');

        this.labels = {
          font: this.querySelector('[data-js-label-font]'),
          symbol: this.querySelector('[data-js-label-symbol]'),
          lines: this.querySelector('[data-js-label-lines]'),
          stamp: this.querySelector('[data-js-label-stamp]'),
          text: this.querySelector('[data-js-label-text]'),
          fontStyle: this.querySelector('[data-js-label-font-style]'),
          month: this.querySelector('[data-js-label-month]'),
          designOption: this.querySelector('[data-js-label-do]'),
          designStyles: this.querySelector('[data-js-label-design-styles]'),
          discStyles: this.querySelector('[data-js-label-disc-styles]'),
          discDesign: this.querySelector('[data-js-label-dd-design]'),
          sideOptions: this.querySelector('[data-js-label-side-options]'),
          optionalPers: this.querySelector('[data-js-label-optional-pers]'),
          state: this.querySelector('[data-js-label-state]'),
          letterCase: this.querySelector('[data-js-label-letter-case]'),
          ringSize: this.querySelector('[data-js-label-ring-size]'),
        };

        this.fields = {
          font: () => this.querySelector('input[name="personalize-font"]:checked'),
          symbol: () => this.querySelector('input[name="personalize-symbol"]:checked'),
        };

        this.headerImg = this.querySelector('[data-js-drawer-product-img]');
        this.headerPrice = this.querySelector('[data-js-drawer-product-price]');
        this.plusOptionsRow = this.querySelector('[data-js-drawer-plus-options]');
        this.plusOptionsAmount = this.querySelector('[data-js-drawer-plus-amount]');
        this.totalExtrasRow = this.querySelector('[data-js-total-extras]');
        this.totalExtrasAmount = this.querySelector('[data-js-total-extras-amount]');

        this.openBtn?.addEventListener('click', this._onOpenClick);
        this.closeBtn?.addEventListener('click', () => this.close());
        this.overlay?.addEventListener('click', this._onOverlayClick);
        this.drawerAtcBtn?.addEventListener('click', this._onDrawerAtc);

        this.addEventListener('change', this._onFieldChange);
        this.addEventListener('input', this._onFieldChange);

        this.variantInput?.addEventListener('change', this._onVariantSync);
        this.form?.querySelectorAll('.lp-pdp__option-label input[type="radio"]').forEach((el) => {
          el.addEventListener('change', this._onVariantSync);
        });

        this._parseVariantData();
        this._syncHeaderFromVariant();
        this._applyMiniHeartVisibility();
        this._applyLineOptionsVisibility();
        this._applySideStampVisibility();
        this._applyOptionalTdReveals();
        this._applyDesignOptionsVisibility();
        this._applyDesignStylesVisibility();
        this._applyEvieSubFields();
        this._applyOptionalPersVisibility();
        this._applyDiscStyleSubFields();
        this._applyDiscExclusiveVisibility();
        this._applyOptionGroupVisibility();

        this.setAttribute('aria-hidden', 'true');
        this.setAttribute('inert', '');
      }

      disconnectedCallback() {
        this.openBtn?.removeEventListener('click', this._onOpenClick);
        this.overlay?.removeEventListener('click', this._onOverlayClick);
        this.drawerAtcBtn?.removeEventListener('click', this._onDrawerAtc);
        this.variantInput?.removeEventListener('change', this._onVariantSync);
        document.removeEventListener('keydown', this._onKeydown);
        document.body.classList.remove('lp-pdp-personalize-drawer-open');
      }

      _parseVariantData() {
        this.variantData = [];
        if (!this.variantDataEl) return;
        try {
          this.variantData = JSON.parse(this.variantDataEl.textContent || '[]');
        } catch {
          this.variantData = [];
        }
      }

      _getSelectedVariant() {
        const id = this.variantInput?.value;
        if (!id || !this.variantData.length) return null;
        return this.variantData.find((v) => String(v.id) === String(id)) || null;
      }

      _formatMoney(cents) {
        const code = this.dataset.currency || 'USD';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(Number(cents) / 100);
        } catch {
          const n = Number(cents) / 100;
          return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
        }
      }

      _syncHeaderFromVariant() {
        const v = this._getSelectedVariant();
        if (v) {
          if (this.headerPrice) {
            /* Mirror any active Shoplift price-test adjustment applied to the
               on-page price block, which is server-rendered with the lowest
               variant price. */
            const renderedCents = this.variantData.length ? Math.min(...this.variantData.map((x) => x.price)) : null;
            const testAdjustment = window.ShopliftPrice.adjustment(document.querySelector('.lp-pdp__price'), renderedCents);
            this.headerPrice.textContent = this._formatMoney(v.price + testAdjustment);
          }

          if (this.headerImg) {
            let src = null;
            if (v.featured_image?.src) {
              src = v.featured_image.src;
            } else if (v.featured_media?.preview_image?.src) {
              src = v.featured_media.preview_image.src;
            }
            if (src) {
              const sized = src.includes('?') ? `${src}&width=200` : `${src}?width=200`;
              this.headerImg.src = sized;
              this.headerImg.alt = this.dataset.productTitle || '';
            }
          }
        }

        this._updatePlusOptionsPrice();
        this._updateTotalExtras();
      }

      /* ── Mini-heart progressive reveal ── */

      _miniHeartMaxSlot() {
        const n = parseInt(this.dataset.miniHeartMaxSlots || '0', 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
      }

      _postMiniHeartGateIsClosed() {
        const g = this.querySelector('[data-js-post-mini-heart-gate]');
        return !!(g && g.hidden);
      }

      _skippedBecausePostMiniHeartGate(el) {
        if (!el) return false;
        return this._postMiniHeartGateIsClosed() && !!el.closest('[data-js-post-mini-heart-gate]');
      }

      _syncPostMiniHeartGate() {
        const gate = this.querySelector('[data-js-post-mini-heart-gate]');
        if (!gate) return;
        const r1 = this.querySelector('[data-js-mini-reveal="1"]');
        gate.hidden = !!(r1 && r1.hidden);
      }

      _applyMiniHeartVisibility() {
        const max = this._miniHeartMaxSlot();
        if (!max) return;

        const reveal = (n) => this.querySelector(`[data-js-mini-reveal="${n}"]`);
        const stackCb = (n) => this.querySelector(`[data-js-mini-stack][data-mini-slot="${n}"]`);
        const stackRow = (n) => this.querySelector(`[data-js-mini-stack-row="${n}"]`);

        const applyReveals = () => {
          let prevRevealVisible = true;
          for (let s = 1; s <= max; s++) {
            const cb = stackCb(s);
            const rev = reveal(s);
            const visible = !!(cb?.checked && prevRevealVisible);
            if (rev) {
              rev.hidden = !visible;
              if (!visible) {
                const sel = rev.querySelector('[data-js-mini-select]');
                if (sel) sel.selectedIndex = 0;
                rev.querySelector('[data-js-label-mini]')?.classList.remove('lp-pdp-drawer__label--error');
              }
            }
            prevRevealVisible = visible;
          }
        };

        applyReveals();

        for (let s = max; s >= 1; s--) {
          if (reveal(s)?.hidden) {
            for (let t = s + 1; t <= max; t++) {
              const c = stackCb(t);
              if (c) c.checked = false;
            }
          }
        }

        applyReveals();

        for (let s = 1; s <= max; s++) {
          const row = stackRow(s);
          if (row) row.hidden = s > 1 && !!reveal(s - 1)?.hidden;
        }

        this._syncPostMiniHeartGate();
        this._updateTotalExtras();
      }

      _updateTotalExtras() {
        if (!this.totalExtrasRow || !this.totalExtrasAmount) return;
        let sumDollars = 0;
        this.querySelectorAll('[data-js-mini-stack]:checked').forEach((cb) => {
          const raw = cb.dataset.addonPriceDollars;
          const p = raw !== undefined ? parseFloat(String(raw), 10) : NaN;
          if (!Number.isNaN(p)) sumDollars += p;
        });
        if (sumDollars <= 0) {
          this.totalExtrasRow.hidden = true;
          this.totalExtrasAmount.textContent = '';
          return;
        }
        this.totalExtrasRow.hidden = false;
        this.totalExtrasAmount.textContent = this._formatMoney(Math.round(sumDollars * 100));
      }

      _updatePlusOptionsPrice() {
        if (!this.plusOptionsRow || !this.plusOptionsAmount) return;
        let sumDollars = 0;
        this.querySelectorAll('[data-js-disc-addon-checkbox]:checked').forEach((cb) => {
          const raw = cb.dataset.addonPriceDollars;
          const p = raw !== undefined ? parseFloat(String(raw), 10) : NaN;
          if (!Number.isNaN(p)) sumDollars += p;
        });
        if (sumDollars <= 0) {
          this.plusOptionsRow.hidden = true;
          this.plusOptionsAmount.textContent = '';
          return;
        }
        this.plusOptionsRow.hidden = false;
        this.plusOptionsAmount.textContent = this._formatMoney(Math.round(sumDollars * 100));
      }

      _toggleDiscAdditional(checkbox) {
        const g = checkbox.dataset.discGroup;
        const idx = checkbox.dataset.addonIndex;
        const block = this.querySelector(
          `[data-js-disc-additional-block][data-disc-group="${g}"][data-addon-index="${idx}"]`
        );
        if (!block) return;
        block.hidden = !checkbox.checked;
        if (!checkbox.checked) {
          const sel = block.querySelector('[data-js-disc-select]');
          if (sel) {
            sel.value = '';
            block.querySelector('[data-js-label-disc]')?.classList.remove('lp-pdp-drawer__label--error');
          }
        }
      }

      /* ── lineOptions: show/hide text inputs based on selected line count ── */

      _applyLineOptionsVisibility() {
        const checked = this.querySelector('[data-js-line-radio]:checked');
        const count = checked ? checked.dataset.lineCount : null;

        for (const group of this.querySelectorAll('[data-js-line-group]')) {
          const isActive = group.dataset.jsLineGroup === count;
          group.hidden = !isActive;
          if (!isActive) {
            for (const inp of group.querySelectorAll('[data-js-text-input]')) {
              inp.value = '';
              this._updateCharCount(inp);
            }
            for (const lbl of group.querySelectorAll('[data-js-label-line-input]')) {
              lbl.classList.remove('lp-pdp-drawer__label--error');
            }
          }
        }
      }

      _stampSubEl(stampVal, scopeRoot) {
        const root = scopeRoot || this;
        const v = stampVal || '';
        for (const sub of root.querySelectorAll('[data-js-stamp-sub]')) {
          if ((sub.getAttribute('data-js-stamp-sub') || '') === v) return sub;
        }
        return null;
      }

      _resetStampSubFields(sub) {
        if (!sub) return;
        const sel = sub.querySelector('[data-js-stamp-sub-select]');
        if (sel) sel.selectedIndex = 0;
        const textInp = sub.querySelector('[data-js-stamp-text-input]');
        if (textInp) {
          textInp.value = '';
          const max = textInp.getAttribute('maxlength') || '0';
          const counter = sub.querySelector('[data-js-char-count]');
          if (counter) {
            const cur = counter.querySelector('[data-js-char-current]');
            if (cur) cur.textContent = '0';
            else counter.textContent = `0 / ${max}`;
          }
        }
        const dateInp = sub.querySelector('[data-js-stamp-date-input]');
        if (dateInp) dateInp.value = '';
        sub.querySelectorAll('[data-js-stamp-font-radio]').forEach((r) => {
          r.checked = false;
        });
        sub.querySelector('[data-js-label-stamp-sub]')?.classList.remove('lp-pdp-drawer__label--error');
        sub.querySelector('[data-js-label-stamp-font]')?.classList.remove('lp-pdp-drawer__label--error');
      }

      _applySideStampVisibility() {
        const wrap2 = this.querySelector('[data-js-stamp-side-2-wrap]');
        if (!wrap2) {
          this._applyStampSubFields();
          return;
        }
        const sideChecked = this.querySelector('[data-js-side-radio]:checked');
        const raw = sideChecked?.value || '';
        const both = raw.toLowerCase().includes('both');
        wrap2.hidden = !both;
        if (!both) {
          wrap2.querySelectorAll('[data-js-stamp-radio]').forEach((r, i) => {
            r.checked = i === 0;
          });
          for (const sub of wrap2.querySelectorAll('[data-js-stamp-sub]')) {
            sub.hidden = true;
            this._resetStampSubFields(sub);
          }
          wrap2.querySelector('[data-js-label-stamp]')?.classList.remove('lp-pdp-drawer__label--error');
        }
        this._applyStampSubFields();
      }

      /* ── Optional text + design checkboxes (requiresOptionalText + requiresOptionalDesign) ── */

      _applyOptionalTdReveals() {
        const textToggle = this.querySelector('[data-js-optional-text-toggle]');
        const designToggle = this.querySelector('[data-js-optional-design-toggle]');
        const textReveal = this.querySelector('[data-js-optional-text-reveal]');
        const designReveal = this.querySelector('[data-js-optional-design-reveal]');

        if (textToggle && textReveal) {
          const textOn = textToggle.checked;
          textReveal.hidden = !textOn;
          if (!textOn) {
            for (const inp of textReveal.querySelectorAll('[data-js-text-input]')) {
              inp.value = '';
              this._updateCharCount(inp);
            }
            for (const name of ['personalize-font', 'personalize-font-style', 'personalize-letter-case']) {
              const radios = textReveal.querySelectorAll(`input[name="${name}"]`);
              radios.forEach((r, i) => {
                r.checked = i === 0;
              });
            }
            for (const lbl of textReveal.querySelectorAll(
              '[data-js-label-text], [data-js-label-text-input], [data-js-label-font], [data-js-label-font-style], [data-js-label-letter-case]'
            )) {
              lbl.classList.remove('lp-pdp-drawer__label--error');
            }
          }
        }

        if (designToggle && designReveal) {
          const designOn = designToggle.checked;
          designReveal.hidden = !designOn;
          if (!designOn) {
            const radios = designReveal.querySelectorAll('[data-js-stamp-radio]');
            radios.forEach((r, i) => {
              r.checked = i === 0;
            });
            for (const sub of designReveal.querySelectorAll('[data-js-stamp-sub]')) {
              sub.hidden = true;
              const sel = sub.querySelector('[data-js-stamp-sub-select]');
              if (sel) sel.selectedIndex = 0;
              const textInp = sub.querySelector('[data-js-stamp-text-input]');
              if (textInp) {
                textInp.value = '';
                const max = textInp.getAttribute('maxlength') || '0';
                const counter = sub.querySelector('[data-js-char-count]');
                if (counter) {
                  const cur = counter.querySelector('[data-js-char-current]');
                  if (cur) cur.textContent = '0';
                  else counter.textContent = `0 / ${max}`;
                }
              }
              const dateInp = sub.querySelector('[data-js-stamp-date-input]');
              if (dateInp) dateInp.value = '';
              sub.querySelectorAll('[data-js-stamp-font-radio]').forEach((r) => {
                r.checked = false;
              });
              sub.querySelector('[data-js-label-stamp-sub]')?.classList.remove('lp-pdp-drawer__label--error');
              sub.querySelector('[data-js-label-stamp-font]')?.classList.remove('lp-pdp-drawer__label--error');
            }
            designReveal.querySelector('[data-js-label-stamp]')?.classList.remove('lp-pdp-drawer__label--error');
          }
          this._applyStampSubFields();
        }
      }

      _optionalTdFontStyleLetterCaseActive() {
        const textToggle = this.querySelector('[data-js-optional-text-toggle]');
        const textReveal = this.querySelector('[data-js-optional-text-reveal]');
        if (textToggle && textReveal) {
          return textToggle.checked && !textReveal.hidden;
        }
        const opFields = this.querySelector('[data-js-optional-pers-fields]');
        if (this._postMiniHeartGateIsClosed() && opFields?.closest('[data-js-post-mini-heart-gate]')) {
          return false;
        }
        return !opFields?.hidden;
      }

      /* ── stampStyles: show/hide sub-fields based on selected stamp ── */

      _applyStampSubFields() {
        if (this.querySelector('[data-js-stamp-sided-root]')) {
          for (const group of this.querySelectorAll('[data-js-stamp-side-group]')) {
            const wrap2 = group.closest('[data-js-stamp-side-2-wrap]');
            if (wrap2?.hidden) continue;
            const checked = group.querySelector('[data-js-stamp-radio]:checked');
            const val = checked?.value || '';
            for (const sub of group.querySelectorAll('[data-js-stamp-sub]')) {
              const subKey = sub.getAttribute('data-js-stamp-sub') || '';
              const isMatch = subKey === val;
              sub.hidden = !isMatch;
              if (!isMatch) this._resetStampSubFields(sub);
            }
          }
          return;
        }

        const checked = this.querySelector('[data-js-stamp-radio]:checked');
        const val = checked?.value || '';

        for (const sub of this.querySelectorAll('[data-js-stamp-sub]')) {
          const subKey = sub.getAttribute('data-js-stamp-sub') || '';
          const isMatch = subKey === val;
          sub.hidden = !isMatch;
          if (!isMatch) this._resetStampSubFields(sub);
        }
      }

      _ddExclusivePanel(label) {
        const v = label || '';
        for (const panel of this.querySelectorAll('[data-js-dd-exclusive-panel]')) {
          if ((panel.getAttribute('data-js-dd-exclusive-panel') || '') === v) return panel;
        }
        return null;
      }

      /* ── discDesigns exclusive: Disc Design radios → Symbol / Text+font / State panels ── */

      _applyDiscExclusiveVisibility() {
        if (!this.querySelector('[data-js-dd-exclusive-root]')) return;
        const checked = this.querySelector('[data-js-dd-design-radio]:checked');
        const val = checked?.value || '';
        for (const panel of this.querySelectorAll('[data-js-dd-exclusive-panel]')) {
          const key = panel.getAttribute('data-js-dd-exclusive-panel') || '';
          const isMatch = key === val;
          panel.hidden = !isMatch;
          if (!isMatch) {
            for (const sel of panel.querySelectorAll('select')) {
              sel.selectedIndex = 0;
            }
            const tw = panel.querySelector('[data-js-dd-exclusive-text-wrap]');
            if (tw) {
              tw.hidden = true;
              const inp = tw.querySelector('[data-js-dd-exclusive-text-input]');
              if (inp) {
                inp.value = '';
                inp.removeAttribute('maxlength');
              }
              const cur = tw.querySelector('[data-js-char-current]');
              if (cur) cur.textContent = '0';
              const maxEl = tw.querySelector('[data-js-dd-char-max]');
              if (maxEl) maxEl.textContent = '0';
            }
            const lbl = panel.querySelector('[data-js-label-dd-exclusive-text]');
            if (lbl) lbl.textContent = 'Text*';
            panel.querySelectorAll('[data-js-label-dd-exclusive-select], [data-js-label-dd-exclusive-font], [data-js-label-dd-exclusive-text]').forEach((el) => {
              el.classList.remove('lp-pdp-drawer__label--error');
            });
          }
        }
        const fontSel = this._ddExclusivePanel(val)?.querySelector('[data-js-dd-exclusive-font-select]');
        if (fontSel) this._applyDiscExclusiveFont(fontSel);
      }

      _applyDiscExclusiveFont(selectEl) {
        const panel = selectEl?.closest?.('[data-js-dd-exclusive-panel]');
        const wrap = panel?.querySelector('[data-js-dd-exclusive-text-wrap]');
        if (!wrap) return;

        const opt = selectEl.options[selectEl.selectedIndex];
        const charLimit = opt ? parseInt(opt.dataset.charLimit || '0', 10) : 0;

        if (!selectEl.value) {
          wrap.hidden = true;
          const inp = wrap.querySelector('[data-js-dd-exclusive-text-input]');
          if (inp) {
            inp.value = '';
            inp.removeAttribute('maxlength');
            inp.dataset.propertyName = 'Text';
          }
          const lbl = wrap.querySelector('[data-js-label-dd-exclusive-text]');
          if (lbl) lbl.textContent = 'Text*';
          const cur = wrap.querySelector('[data-js-char-current]');
          if (cur) cur.textContent = '0';
          const maxEl = wrap.querySelector('[data-js-dd-char-max]');
          if (maxEl) maxEl.textContent = '0';
          return;
        }

        wrap.hidden = false;
        const inp = wrap.querySelector('[data-js-dd-exclusive-text-input]');
        const lbl = wrap.querySelector('[data-js-label-dd-exclusive-text]');
        const maxSpan = wrap.querySelector('[data-js-dd-char-max]');
        const fontName = selectEl.value;
        if (lbl) lbl.textContent = `Text - ${fontName}*`;
        if (inp) {
          inp.dataset.propertyName = `Text - ${fontName}`;
          inp.maxLength = charLimit > 0 ? charLimit : 9999;
          if (inp.value.length > charLimit && charLimit > 0) {
            inp.value = inp.value.slice(0, charLimit);
          }
          this._updateCharCount(inp);
        }
        if (maxSpan) maxSpan.textContent = charLimit > 0 ? String(charLimit) : '∞';
      }

      /* ── discDesigns: font select → show text input, update char limit ── */

      _applyDiscDesignText(selectEl) {
        const idx = selectEl.dataset.ddIndex;
        const wrap = this.querySelector(`[data-js-dd-text-wrap="${idx}"]`);
        if (!wrap) return;

        const opt = selectEl.options[selectEl.selectedIndex];
        const charLimit = opt ? parseInt(opt.dataset.charLimit || '0', 10) : 0;

        if (!selectEl.value) {
          wrap.hidden = true;
          const inp = wrap.querySelector('[data-js-dd-text-input]');
          if (inp) inp.value = '';
          return;
        }

        wrap.hidden = false;
        const inp = wrap.querySelector('[data-js-dd-text-input]');
        const maxSpan = wrap.querySelector('[data-js-dd-char-max]');
        if (inp) {
          inp.maxLength = charLimit > 0 ? charLimit : 9999;
          if (inp.value.length > charLimit && charLimit > 0) {
            inp.value = inp.value.slice(0, charLimit);
          }
          this._updateCharCount(inp);
        }
        if (maxSpan) maxSpan.textContent = charLimit > 0 ? String(charLimit) : '∞';
      }

      /* ── optionGroup + additionalOptionGroups: independent toggles (non-sequential) ── */

      _applyOptionGroupVisibility() {
        const checkboxes = [...this.querySelectorAll('[data-js-og-addon-cb]')].sort(
          (a, b) => Number(a.dataset.ogSlot) - Number(b.dataset.ogSlot)
        );

        for (const cb of checkboxes) {
          const slot = cb.dataset.ogSlot;
          const addonRow = cb.closest('[data-js-og-addon-row]');
          const field = this.querySelector(`[data-js-og-field="${slot}"]`);

          if (addonRow) addonRow.hidden = false;

          if (!cb.checked) {
            if (field) {
              field.hidden = true;
              field.querySelectorAll('[data-js-og-radio]').forEach((r, i) => {
                r.checked = i === 0;
              });
              field.querySelector('[data-js-label-og]')?.classList.remove('lp-pdp-drawer__label--error');
            }
            continue;
          }

          if (field) {
            field.hidden = false;
            if (!field.querySelector('[data-js-og-radio]:checked')) {
              const first = field.querySelector('[data-js-og-radio]');
              if (first) first.checked = true;
            }
          }
        }
      }

      /* ── designOptions: show/hide panels based on selected design ── */

      _applyDesignOptionsVisibility() {
        const radio = this.querySelector('[data-js-do-radio]:checked');
        const sel = this.querySelector('[data-js-do-select]');
        const val = radio?.value || sel?.value || '';
        if (!val && !this.querySelector('[data-js-do-panel]')) return;

        for (const panel of this.querySelectorAll('[data-js-do-panel]')) {
          const isMatch = panel.dataset.jsDoPanel === val;
          panel.hidden = !isMatch;
          if (!isMatch) {
            panel.querySelectorAll('[data-js-do-text]').forEach((inp) => {
              inp.value = '';
              this._updateCharCount(inp);
            });
            panel.querySelectorAll('[data-js-do-field-select]').forEach((s) => { s.selectedIndex = 0; });
            panel.querySelectorAll('[data-js-label-do-field]').forEach((l) => l.classList.remove('lp-pdp-drawer__label--error'));
          }
        }
        this._applyDoLineVisibility();
      }

      _applyDoLineVisibility() {
        for (const root of this.querySelectorAll('[data-js-do-line-root]')) {
          const panel = root.closest('[data-js-do-panel]');
          if (panel?.hidden) continue;
          const doIdx = root.dataset.jsDoLineRoot;
          const checked = root.querySelector('[data-js-do-line-radio]:checked');
          const count = checked?.dataset.lineCount || null;
          for (const group of this.querySelectorAll(`[data-js-do-line-group^="${doIdx}-"]`)) {
            const key = group.dataset.jsDoLineGroup;
            const isActive = key === `${doIdx}-${count}`;
            group.hidden = !isActive;
            if (!isActive) {
              group.querySelectorAll('[data-js-do-text]').forEach((inp) => {
                inp.value = '';
                this._updateCharCount(inp);
              });
            }
          }
        }
      }

      /* ── designStyles: show/hide existing field groups based on style ── */

      _applyDesignStylesVisibility() {
        const checked = this.querySelector('[data-js-design-style-radio]:checked');
        if (!checked) return;
        const val = checked.value.toLowerCase();
        const symbolPanel = this.querySelector('[data-js-design-style-symbol-panel]');
        if (symbolPanel) {
          symbolPanel.hidden = val !== 'symbol';
          if (symbolPanel.hidden) {
            const symSel = symbolPanel.querySelector('[data-js-design-style-symbol-select]');
            if (symSel) symSel.selectedIndex = 0;
            symbolPanel.querySelector('[data-js-label-design-style-symbol]')?.classList.remove(
              'lp-pdp-drawer__label--error'
            );
          }
        }

        const showLines = val === 'line' || val === 'text';
        const lineSection = this.querySelector('[data-js-design-style-line-section]');
        const lineRoot = this.querySelector('[data-js-line-options-root]');
        if (lineSection) {
          lineSection.hidden = !showLines;
        } else if (lineRoot) {
          lineRoot.hidden = !showLines;
        }

        if (lineRoot) {
          for (const group of this.querySelectorAll('[data-js-line-group]')) {
            if (!showLines) group.hidden = true;
          }
          if (showLines) this._applyLineOptionsVisibility();
        }
      }

      /* ── evieStampOptions: per-group sub-field visibility ── */

      _applyEvieSubFields() {
        for (const root of this.querySelectorAll('[data-js-evie-root]')) {
          const idx = root.dataset.jsEvieRoot;
          const checked = root.querySelector('[data-js-evie-radio]:checked');
          const val = checked?.value || '';
          for (const sub of this.querySelectorAll(`[data-js-evie-sub="${idx}"]`)) {
            const isMatch = sub.dataset.evieSubType === val;
            sub.hidden = !isMatch;
            if (!isMatch) {
              const sel = sub.querySelector('[data-js-evie-sub-select]');
              if (sel) sel.selectedIndex = 0;
              sub.querySelector('[data-js-label-evie-sub]')?.classList.remove('lp-pdp-drawer__label--error');
            }
          }
        }
      }

      _discStyleSubEl(styleVal) {
        const v = styleVal || '';
        for (const sub of this.querySelectorAll('[data-js-disc-style-sub]')) {
          if ((sub.getAttribute('data-js-disc-style-sub') || '') === v) return sub;
        }
        return null;
      }

      /* ── discStyles: Initial (A–Z) / Number (1–30) / Symbol (evie symbol list) sub-selects ── */

      _applyDiscStyleSubFields() {
        const checked = this.querySelector('[data-js-disc-style-radio]:checked');
        const val = checked?.value || '';
        for (const sub of this.querySelectorAll('[data-js-disc-style-sub]')) {
          const subType = sub.getAttribute('data-js-disc-style-sub') || '';
          const isMatch = subType === val;
          sub.hidden = !isMatch;
          if (!isMatch) {
            const sel = sub.querySelector('[data-js-disc-style-sub-select]');
            if (sel) sel.selectedIndex = 0;
            sub.querySelector('[data-js-label-disc-style-sub]')?.classList.remove('lp-pdp-drawer__label--error');
          }
        }
      }

      /* ── optionalPersonalization: toggle text / font block (not "Blank / No Text") ── */

      _optionalPersShowsTextAndFontFields(value) {
        const v = String(value || '').toLowerCase().trim();
        if (/\bblank\b/.test(v)) return false;
        if (/\bno\s+text\b/.test(v)) return false;
        if (v === 'no' || v === 'none') return false;
        return true;
      }

      _applyOptionalPersVisibility() {
        const wrap = this.querySelector('[data-js-optional-pers-fields]');
        const checked = this.querySelector('[data-js-optional-pers-radio]:checked');
        if (!wrap || !checked) return;
        const show = this._optionalPersShowsTextAndFontFields(checked.value);
        wrap.hidden = !show;
        if (!show) {
          wrap.querySelectorAll('[data-js-text-input]').forEach((inp) => {
            inp.value = '';
            this._updateCharCount(inp);
          });
          wrap.querySelectorAll('[data-js-label-text-input], [data-js-label-text]').forEach((l) =>
            l.classList.remove('lp-pdp-drawer__label--error')
          );
          wrap.querySelectorAll('input[name="personalize-font"]').forEach((r, i) => {
            r.checked = i === 0;
          });
          this.labels.font?.classList.remove('lp-pdp-drawer__label--error');
          this.labels.text?.classList.remove('lp-pdp-drawer__label--error');
          this.labels.fontStyle?.classList.remove('lp-pdp-drawer__label--error');
          this.labels.letterCase?.classList.remove('lp-pdp-drawer__label--error');
        }
      }

      /* ── Month ring size: update ring size options when month changes ── */

      _applyMonthRingSize() {
        const monthSel = this.querySelector('[data-js-month-select]');
        const ringSizeWrap = this.querySelector('[data-js-month-ring-size-wrap]');
        if (!monthSel || !ringSizeWrap) return;
        const opt = monthSel.options[monthSel.selectedIndex];
        const sizes = opt?.dataset.ringSizes;
        const rsSel = ringSizeWrap.querySelector('[data-js-ring-size-select]');
        if (!rsSel) return;
        if (!sizes) {
          ringSizeWrap.hidden = true;
          return;
        }
        ringSizeWrap.hidden = false;
        const arr = sizes.split(',');
        rsSel.innerHTML = arr.map((s, i) => `<option value="${s}"${i === 0 ? ' selected' : ''}>${s}</option>`).join('');
      }

      /* ── Character counter helper ── */

      _updateCharCount(inputEl) {
        const field = inputEl.closest('.lp-pdp-drawer__text-field, .lp-pdp-drawer__field');
        if (!field) return;
        const current = field.querySelector('[data-js-char-current]');
        if (current) current.textContent = String(inputEl.value.length);
      }

      /* ── Variant sync ── */

      _onVariantSync() {
        requestAnimationFrame(() => this._syncHeaderFromVariant());
      }

      _onOpenClick(e) {
        e.preventDefault();
        // Don't open drawer if button is in "Notify Me" mode for out-of-stock variants
        if (this.openBtn?.dataset.lpBisNotify === 'true') {
          return;
        }
        this.open();
      }

      _onOverlayClick(e) {
        if (e.target === this.overlay) this.close();
      }

      _onKeydown(e) {
        if (e.key === 'Escape') this.close();
      }

      open() {
        this._syncHeaderFromVariant();
        this._updatePlusOptionsPrice();
        this._applyMiniHeartVisibility();
        this._applySideStampVisibility();
        this._applyDesignStylesVisibility();
        this._updateTotalExtras();
        this.setAttribute('data-state', 'open');
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        this.openBtn?.setAttribute('aria-expanded', 'true');
        document.body.style.setProperty('--lp-pdp-drawer-scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);
        document.body.classList.add('lp-pdp-personalize-drawer-open');
        document.addEventListener('keydown', this._onKeydown);
        this.closeBtn?.focus();
      }

      close() {
        this.removeAttribute('data-state');
        this.setAttribute('inert', '');
        this.setAttribute('aria-hidden', 'true');
        this.openBtn?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('lp-pdp-personalize-drawer-open');
        document.removeEventListener('keydown', this._onKeydown);
        this.openBtn?.focus();
      }

      /* ── Validation ── */

      _clearErrors() {
        for (const el of Object.values(this.labels)) {
          el?.classList.remove('lp-pdp-drawer__label--error');
        }
        for (const sel of [
          '[data-js-label-initial]',
          '[data-js-label-disc]',
          '[data-js-label-sg]',
          '[data-js-label-mini]',
          '[data-js-label-line-input]',
          '[data-js-label-stamp-sub]',
          '[data-js-label-stamp-font]',
          '[data-js-label-dd]',
          '[data-js-label-dd-text]',
          '[data-js-label-og]',
          '[data-js-label-text-input]',
          '[data-js-label-text]',
          '[data-js-label-do-field]',
          '[data-js-label-do-line]',
          '[data-js-label-evie-sub]',
          '[data-js-label-disc-style-sub]',
          '[data-js-label-dd-design]',
          '[data-js-label-dd-exclusive-font]',
          '[data-js-label-dd-exclusive-select]',
          '[data-js-label-dd-exclusive-text]',
          '[data-js-label-design-style-symbol]',
          '[data-js-label-stamp]',
        ]) {
          for (const el of this.querySelectorAll(sel)) {
            el.classList.remove('lp-pdp-drawer__label--error');
          }
        }
      }

      _initialIndexFromSelectName(name) {
        const m = typeof name === 'string' ? name.match(/^personalize-initial-(\d+)$/) : null;
        return m ? m[1] : '';
      }

      _validate() {
        this._clearErrors();
        let ok = true;

        /* lineOptions (skip when Lines UI is hidden, e.g. Design = Symbol) */
        const lineOptsRoot = this.querySelector('[data-js-line-options-root]');
        const lineOptsWrap = lineOptsRoot?.closest('[data-js-design-style-line-section]');
        const lineOptsActive =
          lineOptsRoot && !lineOptsRoot.hidden && (!lineOptsWrap || !lineOptsWrap.hidden);
        if (lineOptsActive) {
          const lineChecked = this.querySelector('[data-js-line-radio]:checked');
          if (!lineChecked) {
            this.labels.lines?.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          } else {
            const count = lineChecked.dataset.lineCount;
            const group = this.querySelector(`[data-js-line-group="${count}"]`);
            if (group && !group.hidden) {
              for (const inp of group.querySelectorAll('[data-js-text-input]')) {
                if (!inp.value.trim()) {
                  inp.closest('.lp-pdp-drawer__text-field')
                    ?.querySelector('[data-js-label-line-input]')
                    ?.classList.add('lp-pdp-drawer__label--error');
                  ok = false;
                }
              }
            }
          }
        }

        /* stampStyles */
        const designRevealVal = this.querySelector('[data-js-optional-design-reveal]');
        const designToggleVal = this.querySelector('[data-js-optional-design-toggle]');
        const skipStamp =
          designRevealVal &&
          designToggleVal &&
          (!designToggleVal.checked || designRevealVal.hidden);

        if (this.querySelector('[data-js-stamp-root]') && !skipStamp) {
          if (this.querySelector('[data-js-stamp-sided-root]')) {
            for (const group of this.querySelectorAll('[data-js-stamp-side-group]')) {
              if (group.closest('[data-js-stamp-side-2-wrap]')?.hidden) continue;
              const stampChecked = group.querySelector('[data-js-stamp-radio]:checked');
              if (!stampChecked) {
                group.querySelector('[data-js-label-stamp]')?.classList.add('lp-pdp-drawer__label--error');
                ok = false;
              } else {
                const sub = this._stampSubEl(stampChecked.value, group);
                if (sub && !sub.hidden) {
                  const sel = sub.querySelector('[data-js-stamp-sub-select]');
                  if (sel && !sel.value?.trim()) {
                    sub.querySelector('[data-js-label-stamp-sub]')?.classList.add('lp-pdp-drawer__label--error');
                    ok = false;
                  }
                  const textInp = sub.querySelector('[data-js-stamp-text-input]');
                  if (textInp && !textInp.value.trim()) {
                    sub.querySelector('[data-js-label-stamp-sub]')?.classList.add('lp-pdp-drawer__label--error');
                    ok = false;
                  }
                  const dateInp = sub.querySelector('[data-js-stamp-date-input]');
                  if (dateInp && !dateInp.value.trim()) {
                    sub.querySelector('[data-js-label-stamp-sub]')?.classList.add('lp-pdp-drawer__label--error');
                    ok = false;
                  }
                  const fontRadio = sub.querySelector('[data-js-stamp-font-radio]');
                  if (fontRadio && !sub.querySelector('[data-js-stamp-font-radio]:checked')) {
                    sub.querySelector('[data-js-label-stamp-font]')?.classList.add('lp-pdp-drawer__label--error');
                    ok = false;
                  }
                }
              }
            }
          } else {
            const stampChecked = this.querySelector('[data-js-stamp-radio]:checked');
            if (!stampChecked) {
              this.labels.stamp?.classList.add('lp-pdp-drawer__label--error');
              ok = false;
            } else {
              const sub = this._stampSubEl(stampChecked.value);
              if (sub && !sub.hidden) {
                const sel = sub.querySelector('[data-js-stamp-sub-select]');
                if (sel && !sel.value?.trim()) {
                  sub.querySelector('[data-js-label-stamp-sub]')?.classList.add('lp-pdp-drawer__label--error');
                  ok = false;
                }
                const textInp = sub.querySelector('[data-js-stamp-text-input]');
                if (textInp && !textInp.value.trim()) {
                  sub.querySelector('[data-js-label-stamp-sub]')?.classList.add('lp-pdp-drawer__label--error');
                  ok = false;
                }
                const dateInp = sub.querySelector('[data-js-stamp-date-input]');
                if (dateInp && !dateInp.value.trim()) {
                  sub.querySelector('[data-js-label-stamp-sub]')?.classList.add('lp-pdp-drawer__label--error');
                  ok = false;
                }
                const fontRadio = sub.querySelector('[data-js-stamp-font-radio]');
                if (fontRadio && !sub.querySelector('[data-js-stamp-font-radio]:checked')) {
                  sub.querySelector('[data-js-label-stamp-font]')?.classList.add('lp-pdp-drawer__label--error');
                  ok = false;
                }
              }
            }
          }
        }

        /* discDesigns exclusive */
        if (this.querySelector('[data-js-dd-exclusive-root]')) {
          const rad = this.querySelector('[data-js-dd-design-radio]:checked');
          if (!rad?.value) {
            this.labels.discDesign?.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          } else {
            const panel = this._ddExclusivePanel(rad.value);
            if (panel && !panel.hidden) {
              const strSel = panel.querySelector('[data-js-dd-exclusive-select]');
              if (strSel && !strSel.value?.trim()) {
                panel.querySelector('[data-js-label-dd-exclusive-select]')?.classList.add(
                  'lp-pdp-drawer__label--error'
                );
                ok = false;
              }
              const fontSel = panel.querySelector('[data-js-dd-exclusive-font-select]');
              if (fontSel) {
                if (!fontSel.value?.trim()) {
                  panel.querySelector('[data-js-label-dd-exclusive-font]')?.classList.add(
                    'lp-pdp-drawer__label--error'
                  );
                  ok = false;
                } else {
                  const wrap = panel.querySelector('[data-js-dd-exclusive-text-wrap]');
                  if (wrap && !wrap.hidden) {
                    const inp = panel.querySelector('[data-js-dd-exclusive-text-input]');
                    if (inp && !inp.value.trim()) {
                      panel.querySelector('[data-js-label-dd-exclusive-text]')?.classList.add(
                        'lp-pdp-drawer__label--error'
                      );
                      ok = false;
                    }
                  }
                }
              }
            }
          }
        } else {
          /* discDesigns legacy */
          for (const sel of this.querySelectorAll('[data-js-dd-font-select]')) {
            if (!sel.value?.trim()) {
              sel.closest('.lp-pdp-drawer__field')
                ?.querySelector('[data-js-label-dd]')
                ?.classList.add('lp-pdp-drawer__label--error');
              ok = false;
            } else {
              const idx = sel.dataset.ddIndex;
              const wrap = this.querySelector(`[data-js-dd-text-wrap="${idx}"]`);
              if (wrap && !wrap.hidden) {
                const inp = wrap.querySelector('[data-js-dd-text-input]');
                if (inp && !inp.value.trim()) {
                  wrap.querySelector('[data-js-label-dd-text]')?.classList.add('lp-pdp-drawer__label--error');
                  ok = false;
                }
              }
            }
          }
        }

        /* optionGroup */
        for (const field of this.querySelectorAll('[data-js-og-field]')) {
          if (field.hidden) continue;
          const idx = field.dataset.jsOgField;
          const checked = this.querySelector(`input[name="personalize-og-${idx}"]:checked`);
          if (!checked) {
            field.querySelector('[data-js-label-og]')?.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          }
        }

        /* months */
        const monthSel = this.querySelector('[data-js-month-select]');
        if (monthSel && !monthSel.value?.trim()) {
          this.labels.month?.classList.add('lp-pdp-drawer__label--error');
          ok = false;
        }

        /* textInputs & requiresText (skip if optional) */
        if (this.dataset.optionalText !== 'true') {
          const textToggleV = this.querySelector('[data-js-optional-text-toggle]');
          const textRevealV = this.querySelector('[data-js-optional-text-reveal]');
          for (const inp of this.querySelectorAll('[data-js-text-input]')) {
            if (inp.closest('[data-js-stamp-sub]')) continue;
            if (inp.closest('[data-js-optional-pers-fields]')?.hidden) continue;
            if (inp.closest('[data-js-optional-text-reveal]')) {
              if (textRevealV?.hidden || !textToggleV?.checked) continue;
            }
            if (inp.closest('[data-js-optional-design-reveal]')?.hidden) continue;
            if (inp.closest('[data-js-line-group]')?.hidden) continue;
            if (inp.closest('[data-js-do-panel]')?.hidden) continue;
            if (inp.closest('[data-js-do-line-group]')?.hidden) continue;
            if (this._skippedBecausePostMiniHeartGate(inp)) continue;
            if (inp.dataset.lineGroup) continue;
            if (!inp.value.trim()) {
              const lbl =
                inp.closest('.lp-pdp-drawer__field, .lp-pdp-drawer__text-field')?.querySelector('[data-js-label-text-input]') ||
                inp.closest('.lp-pdp-drawer__field, .lp-pdp-drawer__text-field')?.querySelector('[data-js-label-text]') ||
                inp.closest('.lp-pdp-drawer__field, .lp-pdp-drawer__text-field')?.querySelector('[data-js-label-do-field]');
              lbl?.classList.add('lp-pdp-drawer__label--error');
              ok = false;
            }
          }
        }

        /* fontStyleOptions */
        const fontStyleRad = this.querySelector('[data-js-font-style-radio]');
        if (fontStyleRad && !this._skippedBecausePostMiniHeartGate(fontStyleRad)) {
          if (this._optionalTdFontStyleLetterCaseActive()) {
            const fsChecked = this.querySelector('[data-js-font-style-radio]:checked');
            if (!fsChecked) {
              this.labels.fontStyle?.classList.add('lp-pdp-drawer__label--error');
              ok = false;
            }
          }
        }

        /* selectionGroups */
        const sgIndices = new Set();
        this.querySelectorAll('[data-js-sg-select], [data-js-sg-radio]').forEach((el) => {
          const i = el.dataset.sgIndex;
          if (i !== undefined && i !== '') sgIndices.add(String(i));
        });
        sgIndices.forEach((idx) => {
          const sel = this.querySelector(`[data-js-sg-select][data-sg-index="${idx}"]`);
          if (sel) {
            if (!sel.value?.trim()) {
              this.querySelector(`[data-js-label-sg][data-sg-index="${idx}"]`)?.classList.add(
                'lp-pdp-drawer__label--error'
              );
              ok = false;
            }
            return;
          }
          const checked = this.querySelector(`input[name="personalize-sg-${idx}"]:checked`);
          if (!checked?.value) {
            this.querySelector(`[data-js-label-sg][data-sg-index="${idx}"]`)?.classList.add(
              'lp-pdp-drawer__label--error'
            );
            ok = false;
          }
        });

        /* miniHeart selects: required only when their reveal is visible (stack for that slot checked) */
        this.querySelectorAll('[data-js-mini-select][data-mini-select-required="true"]').forEach((sel) => {
          const reveal = sel.closest('[data-js-mini-reveal]');
          if (!reveal || reveal.hidden) return;
          if (!sel.value?.trim()) {
            reveal.querySelector('[data-js-label-mini]')?.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          }
        });

        /* discCustomizationOptions selects */
        this.querySelectorAll('[data-js-disc-select][data-disc-required="true"]').forEach((sel) => {
          const addBlock = sel.closest('[data-js-disc-additional-block]');
          if (addBlock && addBlock.hidden) return;
          if (!sel.value?.trim()) {
            sel.closest('.lp-pdp-drawer__field')?.querySelector('[data-js-label-disc]')?.classList.add(
              'lp-pdp-drawer__label--error'
            );
            ok = false;
          }
        });

        /* fonts */
        const fontLabelField = this.labels.font?.closest('.lp-pdp-drawer__field');
        if (this.labels.font && !this._skippedBecausePostMiniHeartGate(fontLabelField)) {
          if (this._optionalTdFontStyleLetterCaseActive()) {
            const font = this.fields.font();
            if (!font || !font.value) {
              this.labels.font.classList.add('lp-pdp-drawer__label--error');
              ok = false;
            }
          }
        }

        /* initials */
        this.querySelectorAll('select[name^="personalize-initial-"]').forEach((sel) => {
          if (this._skippedBecausePostMiniHeartGate(sel)) return;
          if (!sel.value?.trim()) {
            const idx = this._initialIndexFromSelectName(sel.name);
            this.querySelector(`[data-js-label-initial][data-initial-index="${idx}"]`)?.classList.add(
              'lp-pdp-drawer__label--error'
            );
            ok = false;
          }
        });

        /* symbols */
        const symbolField = this.labels.symbol?.closest('.lp-pdp-drawer__field');
        if (this.labels.symbol && !this._skippedBecausePostMiniHeartGate(symbolField)) {
          const sym = this.fields.symbol();
          if (!sym || !sym.value) {
            this.labels.symbol.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          }
        }

        /* designOptions */
        if (this.querySelector('[data-js-do-root]')) {
          const doRadio = this.querySelector('[data-js-do-radio]:checked');
          const doSel = this.querySelector('[data-js-do-select]');
          const doVal = doRadio?.value || doSel?.value || '';
          if (!doVal) {
            this.labels.designOption?.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          } else {
            const panel = this.querySelector(`[data-js-do-panel="${doVal}"]`);
            if (panel && !panel.hidden) {
              panel.querySelectorAll('[data-js-do-text]').forEach((inp) => {
                if (inp.closest('[data-js-do-line-group]')?.hidden) return;
                if (!inp.value.trim()) {
                  const lbl = inp.closest('.lp-pdp-drawer__field, .lp-pdp-drawer__text-field')?.querySelector('[data-js-label-do-field]');
                  lbl?.classList.add('lp-pdp-drawer__label--error');
                  ok = false;
                }
              });
              panel.querySelectorAll('[data-js-do-field-select]').forEach((sel) => {
                if (!sel.value?.trim()) {
                  sel.closest('.lp-pdp-drawer__field')?.querySelector('[data-js-label-do-field]')?.classList.add('lp-pdp-drawer__label--error');
                  ok = false;
                }
              });
            }
          }
        }

        /* designStyles */
        if (this.querySelector('[data-js-design-styles-root]')) {
          const dsRad = this.querySelector('[data-js-design-style-radio]:checked');
          if (!dsRad) {
            this.labels.designStyles?.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          } else {
            const dsv = String(dsRad.value || '').toLowerCase();
            if (dsv === 'symbol') {
              const symPanel = this.querySelector('[data-js-design-style-symbol-panel]');
              const symSel = symPanel?.querySelector('[data-js-design-style-symbol-select]');
              if (symPanel && !symPanel.hidden && symSel && !symSel.value?.trim()) {
                symPanel.querySelector('[data-js-label-design-style-symbol]')?.classList.add(
                  'lp-pdp-drawer__label--error'
                );
                ok = false;
              }
            }
          }
        }

        /* discStyles */
        const discStylesRoot = this.querySelector('[data-js-disc-styles-root]');
        if (discStylesRoot && !this._skippedBecausePostMiniHeartGate(discStylesRoot)) {
          const dcsRad = this.querySelector('[data-js-disc-style-radio]:checked');
          if (!dcsRad) {
            this.labels.discStyles?.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          } else {
            const sub = this._discStyleSubEl(dcsRad.value);
            if (sub && !sub.hidden) {
              const sel = sub.querySelector('[data-js-disc-style-sub-select]');
              if (sel && !sel.value?.trim()) {
                sub.querySelector('[data-js-label-disc-style-sub]')?.classList.add('lp-pdp-drawer__label--error');
                ok = false;
              }
            }
          }
        }

        /* evieStampOptions */
        for (const root of this.querySelectorAll('[data-js-evie-root]')) {
          const idx = root.dataset.jsEvieRoot;
          const checked = root.querySelector('[data-js-evie-radio]:checked');
          if (!checked) {
            this.querySelector(`[data-js-label-evie="${idx}"]`)?.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          } else {
            const sub = this.querySelector(`[data-js-evie-sub="${idx}"][data-evie-sub-type="${checked.value}"]`);
            if (sub && !sub.hidden) {
              const sel = sub.querySelector('[data-js-evie-sub-select]');
              if (sel && !sel.value?.trim()) {
                sub.querySelector('[data-js-label-evie-sub]')?.classList.add('lp-pdp-drawer__label--error');
                ok = false;
              }
            }
          }
        }

        /* sideOptions */
        if (this.querySelector('[data-js-side-options-root]')) {
          if (!this.querySelector('[data-js-side-radio]:checked')) {
            this.labels.sideOptions?.classList.add('lp-pdp-drawer__label--error');
            ok = false;
          }
        }

        /* letterCaseOptions */
        const letterCaseRad = this.querySelector('[data-js-letter-case-radio]');
        if (letterCaseRad && !this._skippedBecausePostMiniHeartGate(letterCaseRad)) {
          if (this._optionalTdFontStyleLetterCaseActive()) {
            if (!this.querySelector('[data-js-letter-case-radio]:checked')) {
              this.labels.letterCase?.classList.add('lp-pdp-drawer__label--error');
              ok = false;
            }
          }
        }

        /* ring size (from months) */
        const ringSizeSel = this.querySelector('[data-js-ring-size-select]');
        const ringSizeWrap = this.querySelector('[data-js-month-ring-size-wrap]');
        if (ringSizeSel && ringSizeWrap && !ringSizeWrap.hidden && !ringSizeSel.value?.trim()) {
          this.labels.ringSize?.classList.add('lp-pdp-drawer__label--error');
          ok = false;
        }

        return ok;
      }

      /* ── Field change handler ── */

      _onFieldChange(e) {
        const t = e.target;

        if (t?.matches?.('[data-js-line-radio]')) {
          this._applyLineOptionsVisibility();
          this.labels.lines?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-optional-text-toggle], [data-js-optional-design-toggle]')) {
          const otherText = this.querySelector('[data-js-optional-text-toggle]');
          const otherDesign = this.querySelector('[data-js-optional-design-toggle]');
          if (t.matches('[data-js-optional-text-toggle]') && t.checked && otherDesign) {
            otherDesign.checked = false;
          }
          if (t.matches('[data-js-optional-design-toggle]') && t.checked && otherText) {
            otherText.checked = false;
          }
          this._applyOptionalTdReveals();
        }

        if (t?.matches?.('[data-js-stamp-radio]')) {
          this._applyStampSubFields();
          this.labels.stamp?.classList.remove('lp-pdp-drawer__label--error');
          t.closest('[data-js-stamp-side-group]')?.querySelector('[data-js-label-stamp]')?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-stamp-date-input]')) {
          t.value = t.value.replace(/[^0-9.]/g, '');
          t.closest('.lp-pdp-drawer__field')
            ?.querySelector('[data-js-label-stamp-sub]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-stamp-text-input]')) {
          this._updateCharCount(t);
          t.closest('.lp-pdp-drawer__field')
            ?.querySelector('[data-js-label-stamp-sub]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-stamp-font-radio]')) {
          t.closest('.lp-pdp-drawer__field')
            ?.querySelector('[data-js-label-stamp-font]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-dd-design-radio]')) {
          this._applyDiscExclusiveVisibility();
          this.labels.discDesign?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-dd-exclusive-font-select]')) {
          this._applyDiscExclusiveFont(t);
          t.closest('[data-js-dd-exclusive-panel]')
            ?.querySelector('[data-js-label-dd-exclusive-font]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-dd-exclusive-select]') && t.value?.trim()) {
          t.closest('[data-js-dd-exclusive-panel]')
            ?.querySelector('[data-js-label-dd-exclusive-select]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-dd-font-select]')) {
          this._applyDiscDesignText(t);
          t.closest('.lp-pdp-drawer__field')
            ?.querySelector('[data-js-label-dd]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-og-addon-cb]')) {
          this._applyOptionGroupVisibility();
        }

        if (t?.matches?.('[data-js-og-radio]')) {
          const idx = t.dataset.ogIndex;
          this.querySelector(`[data-js-label-og][data-og-index="${idx}"]`)?.classList.remove(
            'lp-pdp-drawer__label--error'
          );
        }

        if (t?.matches?.('[data-js-text-input]')) {
          this._updateCharCount(t);
          t.closest('.lp-pdp-drawer__field, .lp-pdp-drawer__text-field')
            ?.querySelector('[data-js-label-text-input], [data-js-label-text], [data-js-label-line-input]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-dd-text-input]')) {
          this._updateCharCount(t);
          t.closest('.lp-pdp-drawer__field')
            ?.querySelector('[data-js-label-dd-text]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-dd-exclusive-text-input]')) {
          this._updateCharCount(t);
          t.closest('[data-js-dd-exclusive-text-wrap]')
            ?.querySelector('[data-js-label-dd-exclusive-text]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-font-style-radio]')) {
          this.labels.fontStyle?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-month-select]') && t.value?.trim()) {
          this.labels.month?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-stamp-sub-select]') && t.value?.trim()) {
          t.closest('.lp-pdp-drawer__field')
            ?.querySelector('[data-js-label-stamp-sub]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-disc-addon-checkbox]')) {
          this._toggleDiscAdditional(t);
          this._updatePlusOptionsPrice();
        }

        if (t?.matches?.('[data-js-disc-select]')) {
          t.closest('.lp-pdp-drawer__field')?.querySelector('[data-js-label-disc]')?.classList.remove(
            'lp-pdp-drawer__label--error'
          );
        }

        if (t?.matches?.('[data-js-mini-stack]')) {
          this._applyMiniHeartVisibility();
        }

        if (t?.matches?.('[data-js-sg-select]') && t.value?.trim()) {
          this.querySelector(`[data-js-label-sg][data-sg-index="${t.dataset.sgIndex}"]`)?.classList.remove(
            'lp-pdp-drawer__label--error'
          );
        }

        if (t?.matches?.('[data-js-sg-radio]') && t.checked) {
          this.querySelector(`[data-js-label-sg][data-sg-index="${t.dataset.sgIndex}"]`)?.classList.remove(
            'lp-pdp-drawer__label--error'
          );
        }

        if (t?.matches?.('[data-js-mini-select]')) {
          t.closest('[data-js-mini-reveal]')?.querySelector('[data-js-label-mini]')?.classList.remove(
            'lp-pdp-drawer__label--error'
          );
        }

        if (this.fields.font()?.value) this.labels.font?.classList.remove('lp-pdp-drawer__label--error');

        this.querySelectorAll('select[name^="personalize-initial-"]').forEach((sel) => {
          if (!sel.value?.trim()) return;
          const idx = this._initialIndexFromSelectName(sel.name);
          this.querySelector(`[data-js-label-initial][data-initial-index="${idx}"]`)?.classList.remove(
            'lp-pdp-drawer__label--error'
          );
        });

        if (this.fields.symbol()?.value) this.labels.symbol?.classList.remove('lp-pdp-drawer__label--error');

        if (t?.matches?.('[data-js-do-radio]') || t?.matches?.('[data-js-do-select]')) {
          this._applyDesignOptionsVisibility();
          this.labels.designOption?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-do-line-radio]')) {
          this._applyDoLineVisibility();
        }

        if (t?.matches?.('[data-js-do-text]')) {
          this._updateCharCount(t);
          t.closest('.lp-pdp-drawer__field, .lp-pdp-drawer__text-field')
            ?.querySelector('[data-js-label-do-field]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-do-field-select]') && t.value?.trim()) {
          t.closest('.lp-pdp-drawer__field')
            ?.querySelector('[data-js-label-do-field]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-design-style-radio]')) {
          this._applyDesignStylesVisibility();
          this.labels.designStyles?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-design-style-symbol-select]') && t.value?.trim()) {
          t.closest('[data-js-design-style-symbol-panel]')
            ?.querySelector('[data-js-label-design-style-symbol]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-disc-style-radio]')) {
          this._applyDiscStyleSubFields();
          this.labels.discStyles?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-disc-style-sub-select]') && t.value?.trim()) {
          t.closest('.lp-pdp-drawer__field')
            ?.querySelector('[data-js-label-disc-style-sub]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-evie-radio]')) {
          this._applyEvieSubFields();
        }

        if (t?.matches?.('[data-js-evie-sub-select]') && t.value?.trim()) {
          t.closest('.lp-pdp-drawer__field')
            ?.querySelector('[data-js-label-evie-sub]')
            ?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-side-radio]')) {
          this.labels.sideOptions?.classList.remove('lp-pdp-drawer__label--error');
          this._applySideStampVisibility();
        }

        if (t?.matches?.('[data-js-optional-pers-radio]')) {
          this._applyOptionalPersVisibility();
          this.labels.optionalPers?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-letter-case-radio]')) {
          this.labels.letterCase?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-month-select]')) {
          this._applyMonthRingSize();
        }

        if (t?.matches?.('[data-js-ring-size-select]') && t.value?.trim()) {
          this.labels.ringSize?.classList.remove('lp-pdp-drawer__label--error');
        }

        if (t?.matches?.('[data-js-state-select]') && t.value?.trim()) {
          this.labels.state?.classList.remove('lp-pdp-drawer__label--error');
        }
      }

      /* ── Build line item properties for cart ── */

      _buildLineItemProperties() {
        const properties = {};

        /* lineOptions */
        const lineChecked = this.querySelector('[data-js-line-radio]:checked');
        const lineOptsRootB = this.querySelector('[data-js-line-options-root]');
        const lineOptsWrapB = lineOptsRootB?.closest('[data-js-design-style-line-section]');
        const lineBuildActive =
          lineOptsRootB && !lineOptsRootB.hidden && (!lineOptsWrapB || !lineOptsWrapB.hidden);
        if (lineBuildActive && lineChecked) {
          properties['Lines'] = `${lineChecked.value} Line${Number(lineChecked.value) > 1 ? 's' : ''}`;
          const count = lineChecked.dataset.lineCount;
          const group = this.querySelector(`[data-js-line-group="${count}"]`);
          if (group && !group.hidden) {
            for (const inp of group.querySelectorAll('[data-js-text-input]')) {
              const key = inp.dataset.propertyName;
              if (key && inp.value.trim()) properties[key] = inp.value.trim();
            }
          }
        }

        /* stampStyles */
        const designRevealB = this.querySelector('[data-js-optional-design-reveal]');
        const designToggleB = this.querySelector('[data-js-optional-design-toggle]');
        const skipStampBuild =
          designRevealB &&
          designToggleB &&
          (!designToggleB.checked || designRevealB.hidden);

        if (this.querySelector('[data-js-stamp-sided-root]') && !skipStampBuild) {
          for (const group of this.querySelectorAll('[data-js-stamp-side-group]')) {
            if (group.closest('[data-js-stamp-side-2-wrap]')?.hidden) continue;
            const stampChecked = group.querySelector('[data-js-stamp-radio]:checked');
            if (!stampChecked?.value) continue;
            const inSecond = !!group.closest('[data-js-stamp-side-2-wrap]');
            const pk = inSecond ? 'Side 2 Stamp Design' : 'Side 1 Stamp Design';
            properties[pk] = stampChecked.value;
            const sub = this._stampSubEl(stampChecked.value, group);
            if (sub && !sub.hidden) {
              const sel = sub.querySelector('[data-js-stamp-sub-select]');
              if (sel) {
                const key = sel.dataset.propertyName;
                if (key && sel.value?.trim()) properties[key] = sel.value;
              }
              const textInp = sub.querySelector('[data-js-stamp-text-input]');
              if (textInp) {
                const key = textInp.dataset.propertyName;
                if (key && textInp.value.trim()) properties[key] = textInp.value.trim();
              }
              const dateInp = sub.querySelector('[data-js-stamp-date-input]');
              if (dateInp) {
                const key = dateInp.dataset.propertyName;
                if (key && dateInp.value.trim()) properties[key] = dateInp.value.trim();
              }
              const fontChecked = sub.querySelector('[data-js-stamp-font-radio]:checked');
              if (fontChecked?.value) {
                properties[inSecond ? 'Font 2' : 'Font'] = fontChecked.value;
              }
            }
          }
        } else {
          const stampChecked = this.querySelector('[data-js-stamp-radio]:checked');
          if (stampChecked?.value && !skipStampBuild) {
            properties['Stamp Design'] = stampChecked.value;
            const sub = this._stampSubEl(stampChecked.value);
            if (sub && !sub.hidden) {
              const sel = sub.querySelector('[data-js-stamp-sub-select]');
              if (sel) {
                const key = sel.dataset.propertyName;
                if (key && sel.value?.trim()) properties[key] = sel.value;
              }
              const textInp = sub.querySelector('[data-js-stamp-text-input]');
              if (textInp) {
                const key = textInp.dataset.propertyName;
                if (key && textInp.value.trim()) properties[key] = textInp.value.trim();
              }
              const dateInp = sub.querySelector('[data-js-stamp-date-input]');
              if (dateInp) {
                const key = dateInp.dataset.propertyName;
                if (key && dateInp.value.trim()) properties[key] = dateInp.value.trim();
              }
              const fontChecked = sub.querySelector('[data-js-stamp-font-radio]:checked');
              if (fontChecked?.value) properties['Font'] = fontChecked.value;
            }
          }
        }

        /* discDesigns exclusive */
        if (this.querySelector('[data-js-dd-exclusive-root]')) {
          const rad = this.querySelector('[data-js-dd-design-radio]:checked');
          if (rad?.value) {
            properties['Disc Design'] = rad.value;
            const panel = this._ddExclusivePanel(rad.value);
            if (panel && !panel.hidden) {
              const strSel = panel.querySelector('[data-js-dd-exclusive-select]');
              if (strSel?.value?.trim()) {
                const sk = strSel.dataset.propertyName;
                if (sk) properties[sk] = strSel.value;
              }
              const fontSel = panel.querySelector('[data-js-dd-exclusive-font-select]');
              if (fontSel?.value?.trim()) {
                properties.Font = fontSel.value;
                const inp = panel.querySelector('[data-js-dd-exclusive-text-input]');
                const tk = inp?.dataset.propertyName;
                if (tk && inp?.value?.trim()) properties[tk] = inp.value.trim();
              }
            }
          }
        } else {
          /* discDesigns legacy */
          for (const sel of this.querySelectorAll('[data-js-dd-font-select]')) {
            if (sel.value?.trim()) {
              const key = sel.dataset.propertyName;
              if (key) properties[key] = sel.value;
            }
            const idx = sel.dataset.ddIndex;
            const wrap = this.querySelector(`[data-js-dd-text-wrap="${idx}"]`);
            if (wrap && !wrap.hidden) {
              const inp = wrap.querySelector('[data-js-dd-text-input]');
              const key = inp?.dataset.propertyName;
              if (key && inp?.value?.trim()) properties[key] = inp.value.trim();
            }
          }
        }

        /* optionGroup */
        for (const field of this.querySelectorAll('[data-js-og-field]')) {
          if (field.hidden) continue;
          const idx = field.dataset.jsOgField;
          const checked = this.querySelector(`input[name="personalize-og-${idx}"]:checked`);
          if (checked?.value) {
            const key = checked.dataset.propertyName;
            if (key) properties[key] = checked.value;
          }
        }

        /* months */
        const monthSel = this.querySelector('[data-js-month-select]');
        if (monthSel?.value?.trim()) {
          const key = monthSel.dataset.propertyName || 'Month';
          properties[key] = monthSel.value;
        }

        /* textInputs & requiresText */
        const textToggleB = this.querySelector('[data-js-optional-text-toggle]');
        const textRevealB = this.querySelector('[data-js-optional-text-reveal]');
        for (const inp of this.querySelectorAll('[data-js-text-input]')) {
          if (inp.dataset.lineGroup) continue;
          if (inp.closest('[data-js-stamp-sub]')) continue;
          if (inp.closest('[data-js-optional-pers-fields]')?.hidden) continue;
          if (inp.closest('[data-js-optional-text-reveal]')) {
            if (textRevealB?.hidden || !textToggleB?.checked) continue;
          }
          if (inp.closest('[data-js-optional-design-reveal]')?.hidden) continue;
          if (this._skippedBecausePostMiniHeartGate(inp)) continue;
          const key = inp.dataset.propertyName;
          if (key && inp.value.trim()) properties[key] = inp.value.trim();
        }

        /* fontStyleOptions */
        if (this._optionalTdFontStyleLetterCaseActive()) {
          const fsChecked = this.querySelector('[data-js-font-style-radio]:checked');
          if (fsChecked?.value) properties['Font Style'] = fsChecked.value;
        }

        /* discCustomizationOptions */
        this.querySelectorAll('[data-js-disc-select]').forEach((sel) => {
          const addBlock = sel.closest('[data-js-disc-additional-block]');
          if (addBlock && addBlock.hidden) return;
          const key = sel.dataset.propertyName;
          if (key && sel.value?.trim()) properties[key] = sel.value;
        });

        /* selectionGroups */
        const sgIndices = new Set();
        this.querySelectorAll('[data-js-sg-select], [data-js-sg-radio]').forEach((el) => {
          const i = el.dataset.sgIndex;
          if (i !== undefined && i !== '') sgIndices.add(String(i));
        });
        [...sgIndices]
          .sort((a, b) => Number(a) - Number(b))
          .forEach((idx) => {
            const sel = this.querySelector(`[data-js-sg-select][data-sg-index="${idx}"]`);
            if (sel?.value?.trim()) {
              const k = sel.dataset.propertyName;
              if (k) properties[k] = sel.value;
              return;
            }
            const rad = this.querySelector(`input[name="personalize-sg-${idx}"]:checked`);
            if (rad?.value) {
              const k = rad.dataset.propertyName;
              if (k) properties[k] = rad.value;
            }
          });

        /* miniHeart */
        const maxMini = this._miniHeartMaxSlot();
        for (let k = 1; k <= maxMini; k++) {
          const cb = this.querySelector(`[data-js-mini-stack][data-mini-slot="${k}"]`);
          if (cb?.checked) {
            const sk = cb.dataset.stackPropertyName;
            if (sk) properties[sk] = '✓';
          }
        }
        for (let s = 1; s <= maxMini; s++) {
          const reveal = this.querySelector(`[data-js-mini-reveal="${s}"]`);
          if (!reveal || reveal.hidden) continue;
          const msel = reveal.querySelector('[data-js-mini-select]');
          const mk = msel?.dataset.propertyName;
          if (mk && msel?.value?.trim()) properties[mk] = msel.value;
        }

        /* fonts */
        const fontFieldForBuild = this.labels.font?.closest('.lp-pdp-drawer__field');
        if (
          this.labels.font &&
          !this._skippedBecausePostMiniHeartGate(fontFieldForBuild) &&
          this._optionalTdFontStyleLetterCaseActive()
        ) {
          const f = this.fields.font()?.value;
          if (f) properties.Font = f;
        }

        /* initials */
        this.querySelectorAll('select[name^="personalize-initial-"]').forEach((sel) => {
          if (this._skippedBecausePostMiniHeartGate(sel)) return;
          const key = sel.dataset.propertyName || `Initial #${this._initialIndexFromSelectName(sel.name)}`;
          if (key && sel.value) properties[key] = sel.value;
        });

        /* symbols */
        const symbolFieldForBuild = this.labels.symbol?.closest('.lp-pdp-drawer__field');
        if (this.labels.symbol && !this._skippedBecausePostMiniHeartGate(symbolFieldForBuild)) {
          const s = this.fields.symbol()?.value;
          if (s) properties['Add Symbol'] = s;
        }

        /* designOptions */
        const doRadio = this.querySelector('[data-js-do-radio]:checked');
        const doSel = this.querySelector('[data-js-do-select]');
        const doVal = doRadio?.value || doSel?.value || '';
        if (doVal) {
          properties['Design'] = doVal;
          const panel = this.querySelector(`[data-js-do-panel="${doVal}"]`);
          if (panel && !panel.hidden) {
            panel.querySelectorAll('[data-js-do-text]').forEach((inp) => {
              if (inp.closest('[data-js-do-line-group]')?.hidden) return;
              const key = inp.dataset.propertyName;
              if (key && inp.value.trim()) properties[key] = inp.value.trim();
            });
            panel.querySelectorAll('[data-js-do-field-select]').forEach((sel) => {
              const key = sel.dataset.propertyName;
              if (key && sel.value?.trim()) properties[key] = sel.value;
            });
            panel.querySelectorAll('[data-js-do-field-radio]:checked').forEach((r) => {
              const key = r.dataset.propertyName;
              if (key && r.value) properties[key] = r.value;
            });
            for (const root of panel.querySelectorAll('[data-js-do-line-root]')) {
              const doIdx = root.dataset.jsDoLineRoot;
              const lineChecked = root.querySelector('[data-js-do-line-radio]:checked');
              if (lineChecked) {
                properties[`${doVal} Lines`] = `${lineChecked.value} Line${Number(lineChecked.value) > 1 ? 's' : ''}`;
              }
            }
          }
        }

        /* designStyles */
        const dsChecked = this.querySelector('[data-js-design-style-radio]:checked');
        if (dsChecked?.value) {
          properties.Design = dsChecked.value;
          properties['Design Style'] = dsChecked.value;
          const dsv = String(dsChecked.value || '').toLowerCase();
          if (dsv === 'symbol') {
            const symPanel = this.querySelector('[data-js-design-style-symbol-panel]');
            const symSel = symPanel?.querySelector('[data-js-design-style-symbol-select]');
            if (symPanel && !symPanel.hidden && symSel?.value?.trim()) {
              const sk = symSel.dataset.propertyName || 'Symbol';
              properties[sk] = symSel.value;
            }
          }
        }

        /* discStyles */
        const discStylesRootB = this.querySelector('[data-js-disc-styles-root]');
        if (discStylesRootB && !this._skippedBecausePostMiniHeartGate(discStylesRootB)) {
          const dcsChecked = this.querySelector('[data-js-disc-style-radio]:checked');
          if (dcsChecked?.value) {
            properties['Disc Style'] = dcsChecked.value;
            const discSub = this._discStyleSubEl(dcsChecked.value);
            if (discSub && !discSub.hidden) {
              const dsel = discSub.querySelector('[data-js-disc-style-sub-select]');
              const dkey = dsel?.dataset.propertyName;
              if (dkey && dsel?.value?.trim()) properties[dkey] = dsel.value;
            }
          }
        }

        /* evieStampOptions */
        for (const root of this.querySelectorAll('[data-js-evie-root]')) {
          const idx = root.dataset.jsEvieRoot;
          const checked = root.querySelector('[data-js-evie-radio]:checked');
          if (checked?.value) {
            const labelEl = this.querySelector(`[data-js-label-evie="${idx}"]`);
            const groupName = labelEl?.textContent?.replace('*', '').trim() || `Stamp ${Number(idx) + 1}`;
            properties[groupName] = checked.value;
            const sub = this.querySelector(`[data-js-evie-sub="${idx}"][data-evie-sub-type="${checked.value}"]`);
            if (sub && !sub.hidden) {
              const sel = sub.querySelector('[data-js-evie-sub-select]');
              const key = sel?.dataset.propertyName;
              if (key && sel?.value?.trim()) properties[key] = sel.value;
            }
          }
        }

        /* sideOptions */
        const sideChecked = this.querySelector('[data-js-side-radio]:checked');
        if (sideChecked?.value) properties['Sides'] = sideChecked.value;

        /* optionalPersonalization */
        const opChecked = this.querySelector('[data-js-optional-pers-radio]:checked');
        if (opChecked?.value) properties['Personalization'] = opChecked.value;

        /* state selection */
        const stateSel = this.querySelector('[data-js-state-select]');
        if (stateSel?.value?.trim()) properties['State'] = stateSel.value;

        /* heart checkbox */
        const heartCb = this.querySelector('[data-js-heart-checkbox]');
        if (heartCb?.checked) properties['Add Heart'] = '✓';

        /* letterCaseOptions */
        if (this._optionalTdFontStyleLetterCaseActive()) {
          const lcChecked = this.querySelector('[data-js-letter-case-radio]:checked');
          if (lcChecked?.value) properties['Letter Case'] = lcChecked.value;
        }

        /* ring size (from months) */
        const rsSel = this.querySelector('[data-js-ring-size-select]');
        const rsWrap = this.querySelector('[data-js-month-ring-size-wrap]');
        if (rsSel && rsWrap && !rsWrap.hidden && rsSel.value?.trim()) {
          properties['Ring Size'] = rsSel.value;
        }

        /* Shoplift price test: carry the test-assignment property Shoplift
           wrote into the product form so its cart transform can reprice the
           line. */
        const slptInput = this.form?.querySelector('input[name="properties[_slpt]"]');
        if (slptInput?.value) properties['_slpt'] = slptInput.value;

        /* Personalized flag: this drawer replaces the form submit, so the
           form's hidden input never posts on its own. The cart drawer's
           delivery estimate reads it to pick the personalized lead times. */
        const personalizedInput = this.form?.querySelector('input[name="properties[_personalized]"]');
        if (personalizedInput?.value) properties['_personalized'] = personalizedInput.value;

        return properties;
      }

      _buildMiniHeartLineItems() {
        const items = [];
        this.querySelectorAll('[data-js-mini-stack]:checked').forEach((cb) => {
          const variantId = Number(cb.dataset.addonVariantId);
          if (!variantId) return;
          items.push({
            id: variantId,
            quantity: 1,
          });
        });
        return items;
      }

      /* ── Add to cart ── */

      async _onDrawerAtc(e) {
        e.preventDefault();
        if (!this._validate()) return;

        const variantId = this.variantInput?.value;
        if (!variantId) return;

        const body = {
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: this._buildLineItemProperties(),
            },
            ...this._buildMiniHeartLineItems(),
          ],
        };

        const btn = this.drawerAtcBtn;
        const label = btn?.querySelector('.lp-pdp-drawer__atc-label');
        const originalLabel = (label?.textContent || 'Add to cart').trim();
        const loadingDotsHtml =
          '<span class="lp-pdp__loading-dots"><span class="lp-pdp__loading-dot"></span><span class="lp-pdp__loading-dot"></span><span class="lp-pdp__loading-dot"></span></span>';

        const clearLoadingState = () => {
          if (btn) {
            btn.disabled = false;
            btn.classList.remove('lp-pdp__submit-btn--loading', 'lp-pdp-drawer__atc--loading');
          }
        };

        if (btn) {
          btn.disabled = true;
          btn.classList.add('lp-pdp__submit-btn--loading', 'lp-pdp-drawer__atc--loading');
        }
        if (label) label.innerHTML = loadingDotsHtml;

        try {
          await window.lpPdpFastCartAdd(body);
          clearLoadingState();
          if (label) label.textContent = originalLabel;
          this.close();
        } catch {
          clearLoadingState();
          if (label) label.textContent = 'Try again';
          setTimeout(() => {
            if (label) label.textContent = originalLabel;
          }, 2500);
        }
      }
    }
  );
}
